import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import CheckboxSelectItem from 'components/checkbox-select-item';
import Collapsible from 'components/collapsible';
import Select from 'components/select';
import PotentialSalesValue from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/graphs/potential-sales-value';
import TotalValueBySizeClass from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/graphs/total-value-by-size-class';
import WeightPerWeekGraph from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/graphs/weight-per-week';
import buildPotentialSalesValueSeries from 'views/bluviu-nutrition/features/scenarios-v2/utils/build-potential-sales-value-series';
import buildTotalValueBySizeClassSeries from 'views/bluviu-nutrition/features/scenarios-v2/utils/build-total-value-by-size-class-series';
import buildWeightPerWeekSeries, {
  indexWeightDataByFeedProgram,
} from 'views/bluviu-nutrition/features/scenarios-v2/utils/build-weight-per-week-series';
import createToggleHandler from 'views/bluviu-nutrition/features/scenarios-v2/utils/create-toggle-handler';

import { useScenarioResultState } from 'views/bluviu-nutrition/features/scenarios-v2/context/scenario-result-context';
import { FishGroup } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-result.type';

const PAGE_NAME_ID = 'bluViuNutritionR2.results.graph';

const getFishGroupDisplayValue = (
  fishGroups: FishGroup[],
  selectedFishGroups: string[],
  allFishGroupIds: string[],
  allFishGroupsLabel: string
) => {
  if (selectedFishGroups.length === allFishGroupIds.length && allFishGroupIds.length > 0) {
    return allFishGroupsLabel;
  }

  if (selectedFishGroups.length === 1) {
    return fishGroups.find((fishGroup) => fishGroup.id === selectedFishGroups[0])?.name ?? '';
  }

  return `${selectedFishGroups.length} of ${allFishGroupIds.length} fish groups`;
};

const ScenarioResultGraphTab: React.FC = () => {
  const intl = useIntl();
  const {
    getScenarioPostProcessQuery: { data: postProcessData },
    updateScenarioPostProcessQuery: { isPending: isUpdatingPostProcessData },
  } = useScenarioResultState();
  const { weightGraphData = [], fishGroupSummaries = [], expectedSalesPriceGraphData = [] } = postProcessData ?? {};

  const fishGroups = useMemo(
    () =>
      fishGroupSummaries
        .map((fishGroup) => ({ id: fishGroup.fishGroupId, name: fishGroup.fishgroupName }))
        .filter((fishGroup, index, items) => items.findIndex((item) => item.id === fishGroup.id) === index),
    [fishGroupSummaries]
  );

  const feedPlanNames = useMemo(() => weightGraphData.map((data) => data.feedPlan), [weightGraphData]);
  const fishGroupIds = useMemo(() => fishGroups.map((fishGroup) => fishGroup.id), [fishGroups]);

  const [activeFeedPlans, setActiveFeedPlans] = useState<string[]>([]);
  const [selectedFishGroups, setSelectedFishGroups] = useState<string[]>([]);

  useEffect(() => {
    setActiveFeedPlans(feedPlanNames);
    setSelectedFishGroups(fishGroupIds);
  }, [feedPlanNames, fishGroupIds]);

  const graphWeightIndex = useMemo(() => indexWeightDataByFeedProgram(weightGraphData), [weightGraphData]);
  const weightPerWeekSeries = useMemo(
    () =>
      buildWeightPerWeekSeries({
        feedPlanNames: activeFeedPlans,
        fishGroupIds: selectedFishGroups,
        allFishGroupIds: fishGroupIds,
        graphWeightIndex,
      }),
    [activeFeedPlans, fishGroupIds, graphWeightIndex, selectedFishGroups]
  );

  const valueGraphData = useMemo(
    () =>
      activeFeedPlans.length === 1 ? expectedSalesPriceGraphData.find((data) => data.feedPlan === activeFeedPlans[0]) : undefined,
    [activeFeedPlans, expectedSalesPriceGraphData]
  );
  const totalValueBySizeClassSeries = useMemo(
    () => buildTotalValueBySizeClassSeries(valueGraphData?.graphValues ?? []),
    [valueGraphData]
  );
  const potentialSalesValueSeries = useMemo(
    () => buildPotentialSalesValueSeries(valueGraphData?.graphValues ?? []),
    [valueGraphData]
  );

  const allFishGroupsLabel = intl.formatMessage({ id: `${PAGE_NAME_ID}.allAvgForSite` });
  const fishGroupDisplayValue = getFishGroupDisplayValue(fishGroups, selectedFishGroups, fishGroupIds, allFishGroupsLabel);
  const isValueGraphDisabled = activeFeedPlans.length !== 1;
  const handleFeedPlanChange = createToggleHandler(feedPlanNames, activeFeedPlans, setActiveFeedPlans);
  const handleFishGroupChange = createToggleHandler(fishGroupIds, selectedFishGroups, setSelectedFishGroups);

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" gap={4} alignItems="center">
          <Select
            multiple
            label={intl.formatMessage({ id: `${PAGE_NAME_ID}.showFeedProgram` })}
            value={activeFeedPlans}
            onChange={(e) => handleFeedPlanChange(e.target.value as string[])}
            renderValue={() => `${activeFeedPlans.length} of ${feedPlanNames.length} programs`}>
            <CheckboxSelectItem value="all" checked={activeFeedPlans.length === feedPlanNames.length} divider>
              <FormattedMessage id={`${PAGE_NAME_ID}.allPrograms`} />
            </CheckboxSelectItem>
            {weightGraphData.map((data) => (
              <CheckboxSelectItem key={data.feedPlan} value={data.feedPlan} checked={activeFeedPlans.includes(data.feedPlan)}>
                {data.feedPlan}
              </CheckboxSelectItem>
            ))}
          </Select>
          <Select
            multiple
            label={intl.formatMessage({ id: `${PAGE_NAME_ID}.showResultsFor` })}
            value={selectedFishGroups}
            onChange={(e) => handleFishGroupChange(e.target.value as string[])}
            renderValue={() => fishGroupDisplayValue}>
            <CheckboxSelectItem value="all" checked={selectedFishGroups.length === fishGroupIds.length} divider>
              {allFishGroupsLabel}
            </CheckboxSelectItem>
            {fishGroups.map((fishGroup) => (
              <CheckboxSelectItem key={fishGroup.id} value={fishGroup.id} checked={selectedFishGroups.includes(fishGroup.id)}>
                {fishGroup.name}
              </CheckboxSelectItem>
            ))}
          </Select>
        </Box>
        {isUpdatingPostProcessData && <CircularProgress size={28} color="inherit" />}
      </Box>
      <WeightPerWeekGraph series={weightPerWeekSeries} />
      <Collapsible
        label={
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="body1" fontWeight={500}>
              {intl.formatMessage({ id: `${PAGE_NAME_ID}.potentialSalesValue` })}
            </Typography>
            {isValueGraphDisabled && (
              <Typography variant="body2">{intl.formatMessage({ id: `${PAGE_NAME_ID}.potentialSalesValueDisabled` })}</Typography>
            )}
          </Box>
        }
        open={!isValueGraphDisabled}
        disabled={isValueGraphDisabled}>
        <PotentialSalesValue series={potentialSalesValueSeries} />
      </Collapsible>
      <Collapsible
        label={
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="body1" fontWeight={500}>
              {intl.formatMessage({ id: `${PAGE_NAME_ID}.valueBySizeClass` })}
            </Typography>
            {isValueGraphDisabled && (
              <Typography variant="body2">{intl.formatMessage({ id: `${PAGE_NAME_ID}.valueBySizeClassDisabled` })}</Typography>
            )}
          </Box>
        }
        open={!isValueGraphDisabled}
        disabled={isValueGraphDisabled}>
        <TotalValueBySizeClass series={totalValueBySizeClassSeries} />
      </Collapsible>
    </Box>
  );
};

export default React.memo(ScenarioResultGraphTab);
